/**
 * String-level round-trip transformer between template source (HTML interleaved with Scriban)
 * and DOM-safe HTML the visual (TipTap) editor can parse.
 *
 * Why string-level: Scriban control tags sit in positions the HTML parser relocates — e.g.
 * `{{ for line in lines }}` as loose text between `<tbody>` and `<tr>` gets foster-parented
 * out of the table by any DOM parse. So Scriban must be rewritten into parser-safe markers
 * BEFORE the content ever reaches a DOM:
 *   - `{{ expr }}`                 → `<span data-scriban-expr="expr"></span>` (atomic chip node)
 *   - `{{ for x in y }}…{{ end }}` → `data-scriban-for="x in y"` attribute on the repeated element
 *
 * The attribute-on-element strategy (rather than a wrapper element) is deliberate: a wrapper
 * around a `<tr>` is invalid inside `<tbody>` and would be dropped by both the browser parser
 * and ProseMirror's table schema, while an attribute survives both.
 *
 * Templates using constructs this transformer cannot represent (if-blocks, Scriban inside HTML
 * attributes, multi-element loop bodies, …) yield `{ ok: false, reasons }` so the UI keeps the
 * code view as the source of truth and disables the visual toggle.
 *
 * Pure functions only — no DOM, no TipTap imports — so everything is unit-testable in node.
 */

export interface ParsedTemplate {
  /** Everything through and including the `<body …>` open tag — kept verbatim (incl. `<style>`). */
  prefix: string;
  /** `</body>` onward — kept verbatim. */
  suffix: string;
  /** Concatenated `<style>` contents from the prefix, for cosmetic editor styling only. */
  styleText: string;
  /** Body inner HTML with Scriban rewritten to data-scriban-* markers. */
  editableBodyHtml: string;
}

export type ParseResult =
  | { ok: true; template: ParsedTemplate }
  | { ok: false; reasons: string[] };

const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** Matches one complete HTML tag (open or close), tolerating `>` inside quoted attributes. */
const TAG_REGEX = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;

const SCRIBAN_TOKEN_REGEX = /\{\{([\s\S]*?)\}\}/g;

/** Scriban statements the visual editor deliberately does not represent. */
const UNSUPPORTED_KEYWORDS =
  /^(if|else|elsif|case|when|while|capture|func|include|wrap|ret|break|continue|tablerow|readonly|import|with)\b/;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function unescapeAttr(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&');
}

function excerpt(text: string, max = 60): string {
  const single = text.replace(/\s+/g, ' ').trim();
  return single.length > max ? `${single.slice(0, max)}…` : single;
}

// ---------------------------------------------------------------------------
// Scriban tokenization (pass 1)
// ---------------------------------------------------------------------------

type ScribanToken =
  | { kind: 'expr'; raw: string; inner: string; start: number; end: number }
  | { kind: 'for'; raw: string; spec: string; start: number; end: number }
  | { kind: 'end'; raw: string; start: number; end: number };

interface Tokenization {
  tokens: ScribanToken[];
  /** Text segments between tokens: segments[0] + tokens[0] + segments[1] + … + segments[n]. */
  segments: string[];
  reasons: string[];
}

function tokenizeScriban(body: string): Tokenization {
  const tokens: ScribanToken[] = [];
  const segments: string[] = [];
  const reasons: string[] = [];
  let lastEnd = 0;

  SCRIBAN_TOKEN_REGEX.lastIndex = 0;
  for (let m = SCRIBAN_TOKEN_REGEX.exec(body); m; m = SCRIBAN_TOKEN_REGEX.exec(body)) {
    segments.push(body.slice(lastEnd, m.index));
    lastEnd = m.index + m[0].length;
    const raw = m[0];
    const inner = m[1].trim();
    const span = { raw, start: m.index, end: lastEnd };

    if (m[1].startsWith('~') || m[1].endsWith('~')) {
      reasons.push(`Whitespace-control tag ${excerpt(raw)} is not supported in the visual editor`);
      tokens.push({ kind: 'expr', inner, ...span });
      continue;
    }
    const forMatch = /^for\s+([A-Za-z_]\w*)\s+in\s+(\S[\s\S]*)$/.exec(inner);
    if (forMatch) {
      const spec = `${forMatch[1]} in ${forMatch[2].trim()}`;
      if (/[\r\n;]/.test(spec)) {
        reasons.push(`Loop header ${excerpt(raw)} is too complex for the visual editor`);
      }
      tokens.push({ kind: 'for', spec, ...span });
      continue;
    }
    if (inner === 'end') {
      tokens.push({ kind: 'end', ...span });
      continue;
    }
    if (UNSUPPORTED_KEYWORDS.test(inner)) {
      reasons.push(
        `Scriban block ${excerpt(raw)} is not supported in the visual editor (only expressions and for-loops)`,
      );
      tokens.push({ kind: 'expr', inner, ...span });
      continue;
    }
    if (/[\r\n;]/.test(inner)) {
      reasons.push(`Multi-statement code block ${excerpt(raw)} is not supported in the visual editor`);
      tokens.push({ kind: 'expr', inner, ...span });
      continue;
    }
    tokens.push({ kind: 'expr', inner, ...span });
  }
  segments.push(body.slice(lastEnd));

  if (segments.some((segment) => segment.includes('{{') || segment.includes('}}'))) {
    reasons.push('Unbalanced {{ }} delimiters');
  }

  return { tokens, segments, reasons };
}

// ---------------------------------------------------------------------------
// HTML context classification (pass 2)
// ---------------------------------------------------------------------------

type HtmlContext = 'text' | 'tag' | 'comment' | 'rawtext';

/**
 * Minimal streaming scanner that tracks whether the current position is in plain text, inside a
 * tag (attributes), inside a comment, or inside script/style raw text. It is fed the segments
 * BETWEEN Scriban tokens, so token contents (e.g. `{{ a < b }}`) can never confuse it.
 */
class HtmlContextScanner {
  private state: HtmlContext = 'text';
  private quote: '"' | "'" | null = null;
  private closingTag = false;
  private tagName = '';
  private nameDone = false;
  private lastChar = '';
  private rawCloseTag = '';

  get context(): HtmlContext {
    return this.state;
  }

  scan(segment: string): void {
    let i = 0;
    while (i < segment.length) {
      const ch = segment[i];
      if (this.state === 'text') {
        if (ch === '<') {
          if (segment.startsWith('<!--', i)) {
            this.state = 'comment';
            i += 4;
            continue;
          }
          this.state = 'tag';
          this.quote = null;
          this.closingTag = segment[i + 1] === '/';
          this.tagName = '';
          this.nameDone = false;
          this.lastChar = '';
          i += this.closingTag ? 2 : 1;
          continue;
        }
        i += 1;
      } else if (this.state === 'tag') {
        if (this.quote) {
          if (ch === this.quote) this.quote = null;
        } else if (ch === '"' || ch === "'") {
          this.quote = ch;
        } else if (ch === '>') {
          const name = this.tagName.toLowerCase();
          if (!this.closingTag && this.lastChar !== '/' && (name === 'script' || name === 'style')) {
            this.state = 'rawtext';
            this.rawCloseTag = `</${name}`;
          } else {
            this.state = 'text';
          }
          i += 1;
          continue;
        } else if (!this.nameDone) {
          if (/[a-zA-Z0-9-]/.test(ch)) this.tagName += ch;
          else this.nameDone = true;
        }
        if (!/\s/.test(ch)) this.lastChar = ch;
        i += 1;
      } else if (this.state === 'comment') {
        const close = segment.indexOf('-->', i);
        if (close === -1) return;
        this.state = 'text';
        i = close + 3;
      } else {
        // rawtext: consume until the matching close tag reappears.
        const close = segment.toLowerCase().indexOf(this.rawCloseTag, i);
        if (close === -1) return;
        this.state = 'text';
        i = close; // let the '<' re-enter tag scanning to consume the close tag itself
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Element scanning shared by loop injection and serialization
// ---------------------------------------------------------------------------

interface ElementSpan {
  name: string;
  /** Index just past the opening tag's `>`. */
  openTagEnd: number;
  /** Index just past the element's closing tag (or past the open tag for void/self-closing). */
  end: number;
}

/** Finds the extent of the element whose opening tag starts at `openStart`. Null on malformed HTML. */
function findElementSpan(html: string, openStart: number): ElementSpan | null {
  TAG_REGEX.lastIndex = openStart;
  const open = TAG_REGEX.exec(html);
  if (!open || open.index !== openStart || open[1] === '/') return null;
  const name = open[2].toLowerCase();
  const openTagEnd = open.index + open[0].length;
  if (VOID_ELEMENTS.has(name) || open[3].trimEnd().endsWith('/')) {
    return { name, openTagEnd, end: openTagEnd };
  }
  let depth = 1;
  for (let m = TAG_REGEX.exec(html); m; m = TAG_REGEX.exec(html)) {
    if (m[2].toLowerCase() !== name) continue;
    if (m[1] === '/') {
      depth -= 1;
      if (depth === 0) return { name, openTagEnd, end: m.index + m[0].length };
    } else if (!VOID_ELEMENTS.has(name) && !m[3].trimEnd().endsWith('/')) {
      depth += 1;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// parseTemplate
// ---------------------------------------------------------------------------

/** Sentinels are wrapped in U+0001 control characters, which cannot occur in HTML source. */
const SENT = '\u0001';
const FOR_SENTINEL = (spec: string) => `${SENT}F${escapeAttr(spec)}${SENT}`;
// eslint-disable-next-line no-control-regex -- the control character IS the sentinel marker
const FOR_SENTINEL_REGEX = /\u0001F([^\u0001]*)\u0001/g;
const END_SENTINEL = `${SENT}E${SENT}`;

export function parseTemplate(htmlContent: string): ParseResult {
  // Pass 0 — split the document; the head (with its <style>) is never parsed or modified.
  let prefix = '';
  let suffix = '';
  let body = htmlContent;
  const bodyOpen = /<body\b(?:"[^"]*"|'[^']*'|[^>"'])*>/i.exec(htmlContent);
  if (bodyOpen) {
    const bodyStart = bodyOpen.index + bodyOpen[0].length;
    const closeIdx = htmlContent.toLowerCase().lastIndexOf('</body');
    if (closeIdx > bodyStart) {
      prefix = htmlContent.slice(0, bodyStart);
      suffix = htmlContent.slice(closeIdx);
      body = htmlContent.slice(bodyStart, closeIdx);
    } else {
      prefix = htmlContent.slice(0, bodyStart);
      body = htmlContent.slice(bodyStart);
    }
  }
  let styleText = '';
  const styleRegex = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi;
  for (let m = styleRegex.exec(prefix); m; m = styleRegex.exec(prefix)) {
    styleText += (styleText ? '\n' : '') + m[1];
  }

  // Pass 1 — tokenize Scriban.
  const { tokens, segments, reasons } = tokenizeScriban(body);

  // Pass 2 — flag tokens sitting where the DOM parser would mangle them (or where an
  // attribute-level rewrite would be required, which ProseMirror cannot round-trip).
  const scanner = new HtmlContextScanner();
  tokens.forEach((token, i) => {
    scanner.scan(segments[i]);
    const context = scanner.context;
    if (context === 'tag') {
      reasons.push(`Scriban tag ${excerpt(token.raw)} inside an HTML attribute is not supported in the visual editor`);
    } else if (context === 'comment') {
      reasons.push(`Scriban tag ${excerpt(token.raw)} inside an HTML comment is not supported in the visual editor`);
    } else if (context === 'rawtext') {
      reasons.push(`Scriban tag ${excerpt(token.raw)} inside a script/style element is not supported in the visual editor`);
    }
  });

  // for/end balance is checkable from the token list alone — report it alongside other reasons.
  let depth = 0;
  for (const token of tokens) {
    if (token.kind === 'for') depth += 1;
    else if (token.kind === 'end') {
      depth -= 1;
      if (depth < 0) break;
    }
  }
  if (depth !== 0) reasons.push('Unbalanced {{ for }} / {{ end }} blocks');

  if (reasons.length > 0) return { ok: false, reasons };

  // Pass 3 — rewrite expressions to marker spans; for/end to sentinels for pass 4.
  let marked = '';
  tokens.forEach((token, i) => {
    marked += segments[i];
    if (token.kind === 'expr') marked += `<span data-scriban-expr="${escapeAttr(token.inner)}"></span>`;
    else if (token.kind === 'for') marked += FOR_SENTINEL(token.spec);
    else marked += END_SENTINEL;
  });
  marked += segments[segments.length - 1];

  // Pass 4 — pair sentinels innermost-first and move each loop onto its repeated element.
  for (;;) {
    const endIdx = marked.indexOf(END_SENTINEL);
    if (endIdx === -1) break;
    FOR_SENTINEL_REGEX.lastIndex = 0;
    let forMatch: RegExpExecArray | null = null;
    for (let m = FOR_SENTINEL_REGEX.exec(marked); m && m.index < endIdx; m = FOR_SENTINEL_REGEX.exec(marked)) {
      forMatch = m; // nearest preceding for-sentinel = innermost
    }
    if (!forMatch) return { ok: false, reasons: ['Unbalanced {{ for }} / {{ end }} blocks'] };

    const spec = unescapeAttr(forMatch[1]);
    const bodyStart = forMatch.index + forMatch[0].length;
    const loopBody = marked.slice(bodyStart, endIdx);
    const trimmed = loopBody.trim();
    const leadingWs = loopBody.length - loopBody.trimStart().length;

    const fail = (why: string): ParseResult => ({
      ok: false,
      reasons: [`Loop "{{ for ${spec} }}" ${why} — the visual editor needs exactly one element as the loop body`],
    });
    if (!/^<[a-zA-Z]/.test(trimmed)) return fail('does not wrap a single HTML element');
    const span = findElementSpan(marked, bodyStart + leadingWs);
    if (!span) return fail('wraps malformed HTML');
    if (span.end > endIdx || marked.slice(span.end, endIdx).trim() !== '') {
      return fail('wraps more than one element');
    }
    const openTag = marked.slice(bodyStart + leadingWs, span.openTagEnd);
    if (openTag.includes('data-scriban-for=')) return fail('targets an element that already carries a loop');

    const injected =
      openTag.replace(/(\/?)>$/, ` data-scriban-for="${escapeAttr(spec)}"$1>`) +
      marked.slice(span.openTagEnd, span.end);
    marked = marked.slice(0, forMatch.index) + injected + marked.slice(endIdx + END_SENTINEL.length);
  }

  return { ok: true, template: { prefix, suffix, styleText, editableBodyHtml: marked } };
}

// ---------------------------------------------------------------------------
// serializeTemplate
// ---------------------------------------------------------------------------

/**
 * Reverses the marker rewrite on edited HTML (typically TipTap's `getHTML()` output) and
 * reassembles the full template source. Chip spans become `{{ expr }}` again and each
 * `data-scriban-for` element is re-wrapped in `{{ for … }}` / `{{ end }}` lines.
 */
export function serializeTemplate(prefix: string, suffix: string, editedBodyHtml: string): string {
  let html = editedBodyHtml.replace(
    /<span(?:"[^"]*"|'[^']*'|[^>"'])*data-scriban-expr="([^"]*)"(?:"[^"]*"|'[^']*'|[^>"'])*>[\s\S]*?<\/span>/g,
    (_, expr: string) => `{{ ${unescapeAttr(expr)} }}`,
  );

  // Restore loops innermost-last: rewriting the first remaining marker each iteration and
  // rescanning handles nesting without any position bookkeeping.
  for (;;) {
    const attrMatch = /\sdata-scriban-for="([^"]*)"/.exec(html);
    if (!attrMatch) break;
    const spec = unescapeAttr(attrMatch[1]);
    const openStart = html.lastIndexOf('<', attrMatch.index);
    const stripped = html.slice(0, attrMatch.index) + html.slice(attrMatch.index + attrMatch[0].length);
    const span = findElementSpan(stripped, openStart);
    if (!span) {
      // Malformed HTML around the marker — keep the attribute out but leave content untouched.
      html = stripped;
      continue;
    }
    const lineStart = stripped.lastIndexOf('\n', openStart) + 1;
    const indent = /^[ \t]*$/.test(stripped.slice(lineStart, openStart))
      ? stripped.slice(lineStart, openStart)
      : '';
    html =
      stripped.slice(0, openStart) +
      `{{ for ${spec} }}\n${indent}` +
      stripped.slice(openStart, span.end) +
      `\n${indent}{{ end }}` +
      stripped.slice(span.end);
  }

  return prefix + html + suffix;
}

// ---------------------------------------------------------------------------
// Fidelity helpers
// ---------------------------------------------------------------------------

/**
 * Ordered inventory of Scriban markers in marker-form HTML — the contract checked before and
 * after a TipTap round trip: no placeholder or loop may silently vanish or change.
 */
export function extractScribanInventory(markedHtml: string): string[] {
  const inventory: string[] = [];
  const regex = /data-scriban-(expr|for)="([^"]*)"/g;
  for (let m = regex.exec(markedHtml); m; m = regex.exec(markedHtml)) {
    inventory.push(`${m[1]}:${unescapeAttr(m[2]).trim()}`);
  }
  return inventory;
}

// ---------------------------------------------------------------------------
// Cosmetic style scoping
// ---------------------------------------------------------------------------

/**
 * Best-effort rewrite of the template's `<style>` rules so they apply inside the visual editor
 * container (`.{scopeClass}`) without leaking into the app. Purely cosmetic — never touches
 * saved content — so on any surprise it degrades to '' (unstyled editing still works).
 */
export function scopeTemplateStyles(cssText: string, scopeClass: string): string {
  try {
    let css = cssText.replace(/\/\*[\s\S]*?\*\//g, '');
    // Drop at-rules entirely (page/print rules are meaningless inside the editor pane).
    css = css.replace(/@[a-zA-Z-]+[^{};]*;/g, '');
    for (;;) {
      const at = css.search(/@[a-zA-Z-]+[^{}]*\{/);
      if (at === -1) break;
      let depth = 0;
      let end = -1;
      for (let i = css.indexOf('{', at); i < css.length; i += 1) {
        if (css[i] === '{') depth += 1;
        else if (css[i] === '}') {
          depth -= 1;
          if (depth === 0) {
            end = i + 1;
            break;
          }
        }
      }
      if (end === -1) return '';
      css = css.slice(0, at) + css.slice(end);
    }
    if (css.includes('@')) return '';

    const scoped: string[] = [];
    for (const rule of css.split('}')) {
      if (!rule.trim()) continue;
      const brace = rule.indexOf('{');
      if (brace === -1) return '';
      const selectors = rule.slice(0, brace).split(',');
      const decls = rule.slice(brace + 1);
      const scopedSelectors = selectors
        .map((selector) => selector.trim())
        .filter(Boolean)
        .map((selector) =>
          /^(html|body)$/i.test(selector)
            ? `.${scopeClass}`
            : /^(html|body)\b/i.test(selector)
              ? selector.replace(/^(html|body)\b/i, `.${scopeClass}`)
              : `.${scopeClass} ${selector}`,
        );
      if (scopedSelectors.length === 0) return '';
      scoped.push(`${scopedSelectors.join(', ')} {${decls}}`);
    }
    return scoped.join('\n');
  } catch {
    return '';
  }
}
